import { useAppDispatch } from 'app/hooks';
import { FETCH_ALL_PARAMS } from 'constants/query';
import { useGetNestedProductCategoriesQuery } from 'features/nested-product-category/services';
import { useGetNestedProductsQuery } from 'features/nested-product/services';
import { useGetPlacesQuery } from 'features/place/services';
import { useGetProductCategoriesQuery } from 'features/product-category/services';
import { useForm, ValidationErrorsType, ValidationErrorType } from 'hooks/useForm';
import { useRole } from 'hooks/useRole';
import { useWorkspace } from 'hooks/useWorkspace';
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useAddDocumentMutation, useDeleteDocumentMutation } from 'services/documents';
import { setErrorMessage, setSuccessMessage } from 'store/toast-slice';
import { PlaceConfigData } from 'types/dtos';
import { ServerErrorType } from 'types/server-responses';
import { dataURLtoFile } from 'utils/file-helpers';
import { trimValues } from 'utils/object-helpers';
import { handleConfigFieldChange } from 'utils/products-config-price-helper';
import { validateConfigFields } from 'utils/products-config-validation-helper';
import { PRODUCT_FIELDS_SCHEMA, PRODUCT_INITIAL_FIELDS } from '../constants/product-fields';
import { useEditProductMutation, useGetProductNestedProductsQuery, useGetProductWithDocumentQuery } from '../services';
import { Product } from '../types/dtos';
import useToggle from 'hooks/useToggle';
import { PRODUCT_STATUS } from '../constants/status';

type AllErrorsType<T> = ValidationErrorsType<T> & {
    [key: string]: ValidationErrorType | undefined;
};

const useController = () => {
    const { productId, placeId = '' } = useParams();
    const { websiteId } = useWorkspace();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [editProduct, { isLoading: isEditProductLoading }] = useEditProductMutation();
    const [addDocument, { isLoading: isAddDocumentLoading }] = useAddDocumentMutation();
    const [deleteDocument, { isLoading: isDeleteDocumentLoading }] = useDeleteDocumentMutation();
    const [selectedNestedProducts, setSelectedNestedProducts] = useState<string[]>([]);
    const [isSelectedNestedProductsDialogOpen, toggleSelectedNestedProductsDialog] = useToggle(false);
    const { data: product } = useGetProductWithDocumentQuery(productId as string);

    const { data: places, isLoading: isGetPlacesLoading } = useGetPlacesQuery({
        ...FETCH_ALL_PARAMS,
        website_id: websiteId,
    });
    const { data: productCategories, isLoading: isGetProductCategoriesLoading } =
        useGetProductCategoriesQuery(FETCH_ALL_PARAMS);
    const { data: nesteProductCategories, isSuccess: isSuccessNestedProductCategories } =
        useGetNestedProductCategoriesQuery({
            website_id: websiteId,
            ...FETCH_ALL_PARAMS,
        });
    const { data: nestedProducts, isSuccess: isSuccessNestedProducts } = useGetNestedProductsQuery({
        website_id: websiteId,
        ...FETCH_ALL_PARAMS,
    });

    const { data: initalNestedProducts, isSuccess: isSuccessInitalNestedProducts } = useGetProductNestedProductsQuery({
        placeId,
        productId: productId as string,
    });

    const isSelectedNestedProductChanged = useMemo(() => {
        return (
            selectedNestedProducts.length !== initalNestedProducts?.length ||
            selectedNestedProducts.some((id) => !initalNestedProducts.some((product) => product.id === id))
        );
    }, [selectedNestedProducts, initalNestedProducts]);

    const nestedCategoriesWithNestedProducts = useMemo(() => {
        if (!isSuccessNestedProductCategories || !isSuccessNestedProducts) return [];

        const categoriesWithProducts = nesteProductCategories.content.map((category) => {
            const products = nestedProducts.content.filter(
                (product) => product.nestedProductCategoryId === category.id,
            );
            return {
                ...category,
                products,
            };
        });

        return categoriesWithProducts;
    }, [isSuccessNestedProductCategories, isSuccessNestedProducts, nesteProductCategories, nestedProducts]);

    const { formData, setInitialFormData, handleChange, handleSubmit, errors, isDirty, changedValues } = useForm({
        validationSchema: PRODUCT_FIELDS_SCHEMA,
        options: {
            trackChangedValues: true,
        },
    });
    const [placeConfigData, setPlaceConfigData] = useState<PlaceConfigData[]>([]);
    const [updateAll, setUpdateAll] = useState<boolean>(false);
    const [priceErrors, setPriceErrors] = useState<Record<string, ValidationErrorType>>({});
    const { isAdmin } = useRole();
    const allErrors: AllErrorsType<typeof PRODUCT_INITIAL_FIELDS> = useMemo(() => {
        return {
            ...errors,
            ...priceErrors,
        };
    }, [errors, priceErrors]);
    const initialPlaceConfigsRef = useRef<PlaceConfigData[]>([]);

    useEffect(() => {
        const place = places?.content.find((place) => place.id === placeId);
        const placeConfig = product?.placeProductConfig?.find((placeConfig) => placeConfig.placeId === placeId);

        if (product && place && placeConfig) {
            const newPlaceConfigs = [
                {
                    id: placeConfig?.id,
                    placeId: place.id,
                    placeName: place?.name,
                    price: placeConfig?.price.toString(),
                    discountedPrice: placeConfig?.discountedPrice ? placeConfig.discountedPrice.toString() : '',
                    isAvailable: placeConfig?.isAvailable
                        ? `${PRODUCT_STATUS.AVAILABLE}`
                        : `${PRODUCT_STATUS.UNAVAILABLE}`,
                },
            ];

            setPlaceConfigData(newPlaceConfigs);

            if (initialPlaceConfigsRef.current.length === 0) {
                initialPlaceConfigsRef.current = [...newPlaceConfigs];
            }

            delete product.placeConfigs;
            setInitialFormData({ ...PRODUCT_INITIAL_FIELDS, ...product });
        }
    }, [product, setInitialFormData, placeId, places?.content]);

    const hasPlaceConfigsChanged = useMemo(() => {
        if (initialPlaceConfigsRef.current.length !== placeConfigData.length) {
            return true;
        }
        if (updateAll) {
            return true;
        }

        return !initialPlaceConfigsRef.current.every((initialConfig, index) => {
            const currentConfig = placeConfigData[index];
            return (
                initialConfig.price === currentConfig.price &&
                initialConfig.discountedPrice === currentConfig.discountedPrice &&
                initialConfig.isAvailable === currentConfig.isAvailable &&
                initialConfig.placeId === currentConfig.placeId
            );
        });
    }, [placeConfigData, updateAll]);

    const validatePrices = useCallback(
        (configs: PlaceConfigData[]): Record<string, ValidationErrorType> => {
            return validateConfigFields<PlaceConfigData>(configs, 'placeId', ['price', 'discountedPrice'], {
                price: {
                    required: {
                        message: t('PRODUCT_FORM_VALIDATIONS.PRICE_REQUIRED'),
                    },
                    isNumber: {
                        message: t('PRODUCT_FORM_VALIDATIONS.PRICE_NUMBER'),
                    },
                    isPositive: {
                        message: t('PRODUCT_FORM_VALIDATIONS.PRICE_POSITIVE'),
                    },
                },
                discountedPrice: {
                    isNumber: {
                        message: t('PRODUCT_FORM_VALIDATIONS.DISCOUNTED_PRICE_NUMBER'),
                    },
                    isPositive: {
                        message: t('PRODUCT_FORM_VALIDATIONS.DISCOUNTED_PRICE_POSITIVE'),
                    },
                    custom: [
                        {
                            validator: (value, allValues) =>
                                !allValues.price.trim() || Number(value) < Number(allValues.price),
                            message: t('PRODUCT_FORM_VALIDATIONS.DISCOUNTED_PRICE_LOWER_THAN_PRICE'),
                        },
                    ],
                },
            });
        },
        [t],
    );

    const handlePlaceConfigPriceChange = useCallback(
        (field: string, value: string, placeId?: string) => {
            handleConfigFieldChange({
                field,
                value,
                id: placeId,
                currentData: placeConfigData,
                setData: setPlaceConfigData,
                errorState: priceErrors,
                setErrorState: setPriceErrors,
                validateFn: validatePrices,
                errorPrefixes: ['price-', 'discountedPrice-'],
            });
        },
        [placeConfigData, priceErrors, setPlaceConfigData, setPriceErrors, validatePrices],
    );

    const handleSucces = useCallback(() => {
        dispatch(setSuccessMessage({ key: 'product.PRODUCT_EDITED' }));
    }, [dispatch]);

    const handleErrors = useCallback(
        (error: ServerErrorType) => {
            const code = error.data?.errorCode;
            dispatch(setErrorMessage({ key: code }));
        },
        [dispatch],
    );

    const getPayload = useCallback(
        (documentId?: string) => {
            const data = { ...changedValues };
            if (documentId) data.documentId = documentId;
            delete data.logo;

            let configsToUse = placeConfigData;
            if (updateAll) {
                const baseConfig = placeConfigData[0];
                const updatedConfigs =
                    places?.content
                        ?.filter((place) => place.id !== baseConfig?.placeId)
                        .map((place) => ({
                            id: product?.placeProductConfig?.find((placeConfig) => placeConfig.placeId === place.id)
                                ?.id,
                            placeId: place.id,
                            placeName: place.name,
                            price: baseConfig?.price,
                            discountedPrice: baseConfig?.discountedPrice,
                            isAvailable: baseConfig?.isAvailable,
                        })) || [];

                configsToUse = [...placeConfigData, ...updatedConfigs];
            }

            const placeConfigsPayload = configsToUse.map((config) => {
                const { placeId, isAvailable, price, discountedPrice } = config;
                return {
                    placeId,
                    price: Number(price),
                    discountedPrice: discountedPrice ? Number(discountedPrice) : null,
                    isAvailable: isAvailable === `${PRODUCT_STATUS.AVAILABLE}` ? true : false,
                };
            });
            data.placeConfigs = placeConfigsPayload;

            return trimValues(data);
        },
        [changedValues, placeConfigData, updateAll, places?.content, product?.placeProductConfig],
    );

    const onSubmit = useCallback(
        (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            handleSubmit(async () => {
                let documentId: string | undefined;
                const prevDocument = product?.documentId;
                try {
                    if (changedValues?.logo) {
                        documentId = (await addDocument(dataURLtoFile(changedValues.logo)).unwrap()).documentId;
                    }

                    const payload = getPayload(documentId);

                    if (isSelectedNestedProductChanged) payload.nestedProductIds = selectedNestedProducts;

                    await editProduct({ id: productId as string, payload }).unwrap();
                    if (changedValues?.logo && prevDocument) {
                        await deleteDocument(prevDocument).unwrap();
                    }

                    handleSucces();

                    navigate(-1);
                } catch (error) {
                    if (documentId) await deleteDocument(documentId).unwrap();
                    handleErrors(error as ServerErrorType);
                }
            });
        },
        [
            handleSubmit,
            product?.documentId,
            changedValues.logo,
            getPayload,
            selectedNestedProducts,
            editProduct,
            productId,
            handleSucces,
            navigate,
            addDocument,
            deleteDocument,
            handleErrors,
            isSelectedNestedProductChanged,
        ],
    );

    const onCancel = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    const onSelectNestedProductComplete = useCallback((selectedProducts: string[]) => {
        setSelectedNestedProducts(selectedProducts);
    }, []);

    useEffect(() => {
        if (isSuccessInitalNestedProducts) {
            const selectedProductIds = initalNestedProducts.map((np) => np.id);
            setSelectedNestedProducts(selectedProductIds);
        }
    }, [isSuccessInitalNestedProducts, initalNestedProducts]);

    return {
        formData: formData as Product,
        places,
        productCategories,
        onChange: handleChange,
        onSubmit,
        onCancel,
        dataChanged: isDirty || hasPlaceConfigsChanged || isSelectedNestedProductChanged,
        isLoading: formData === undefined || isGetPlacesLoading || isGetProductCategoriesLoading,
        isEditProductLoading: isEditProductLoading || isAddDocumentLoading || isDeleteDocumentLoading,
        errors: allErrors,
        isAdmin,
        placeConfigData,
        isEdit: true,
        updateAll,
        setUpdateAll,
        onConfigPriceChange: handlePlaceConfigPriceChange,
        placeId,
        nestedCategoriesWithNestedProducts,
        onSelectNestedProductComplete,
        selectedNestedProducts,
        toggleSelectedNestedProductsDialog,
        isSelectedNestedProductsDialogOpen,
    };
};

export default useController;
